import { ApiRoutes } from "@/enums/apiRoutes.enum";
import axios from "@/lib/axios";
import {
  AutomaticCampaignDto,
  CampaignMetrics,
  CampaignPayload,
  CampaignPreviewResponse,
  CatalogItem,
  ManualCampaignDto,
  ProductGroupDto,
  TargetSegment,
} from "@/types/campaignApi";

/**
 * Manual-campaigns API. All calls go through the v2 BFF proxy
 * (src/pages/api/v2/[...slug].ts), which injects the bearer token server-side.
 * Endpoints follow the as-built `manual-campaigns` namespace
 * (documentation/CAMPAIGN_BUILDER_GUIDE.md + dashboard-v2 prototype).
 */
export const campaignService = {
  async list(): Promise<ManualCampaignDto[]> {
    const response = await axios.get<ManualCampaignDto[]>(
      ApiRoutes.MANUAL_CAMPAIGNS,
    );
    return response.data;
  },

  /** Full campaign row (target_segment, reward, pushes…) — used to edit a draft. */
  async get(id: string): Promise<CampaignPayload> {
    const response = await axios.get<CampaignPayload>(
      `${ApiRoutes.MANUAL_CAMPAIGNS}/${id}`,
    );
    return response.data;
  },

  /** Create a campaign. The wire payload carries no `id` (server assigns it). */
  async create(
    payload: Omit<CampaignPayload, "id">,
  ): Promise<ManualCampaignDto> {
    const response = await axios.post<ManualCampaignDto>(
      ApiRoutes.MANUAL_CAMPAIGNS,
      payload,
    );
    return response.data;
  },

  async update(
    id: string,
    payload: Partial<Omit<CampaignPayload, "id">>,
  ): Promise<ManualCampaignDto> {
    const response = await axios.patch<ManualCampaignDto>(
      `${ApiRoutes.MANUAL_CAMPAIGNS}/${id}`,
      payload,
    );
    return response.data;
  },

  /** Launch a draft/scheduled campaign — enqueues it (or schedules if future). */
  async launch(id: string): Promise<void> {
    await axios.post(`${ApiRoutes.MANUAL_CAMPAIGNS}/${id}/launch`);
  },

  /** Cancel/stop a campaign (moves it to `cancelled`). */
  async cancel(id: string): Promise<void> {
    await axios.post(`${ApiRoutes.MANUAL_CAMPAIGNS}/${id}/cancel`);
  },

  async metrics(id: string, refresh = false): Promise<CampaignMetrics> {
    const response = await axios.get<CampaignMetrics>(
      `${ApiRoutes.MANUAL_CAMPAIGNS}/${id}/metrics${refresh ? "?refresh=1" : ""}`,
    );
    return response.data;
  },

  /** Audience size estimate for the segment being built. */
  async preview(
    targetSegment: TargetSegment,
  ): Promise<CampaignPreviewResponse> {
    const response = await axios.post<CampaignPreviewResponse>(
      ApiRoutes.MANUAL_CAMPAIGNS_PREVIEW,
      { target_segment: targetSegment },
    );
    return response.data;
  },

  async categories(): Promise<CatalogItem[]> {
    const response = await axios.get<CatalogItem[]>(
      ApiRoutes.MANUAL_CAMPAIGNS_CATEGORIES,
    );
    return response.data;
  },

  async stores(): Promise<CatalogItem[]> {
    const response = await axios.get<CatalogItem[]>(
      ApiRoutes.MANUAL_CAMPAIGNS_STORES,
    );
    return response.data;
  },

  async productGroups(): Promise<ProductGroupDto[]> {
    const response = await axios.get<ProductGroupDto[]>(
      ApiRoutes.MANUAL_CAMPAIGNS_PRODUCT_GROUPS,
    );
    return response.data;
  },

  /** Automatic campaigns (campaign_configs) — enabled state + params per key. */
  async listAutomatic(
    tenant: "fish" | "meat" = "fish",
  ): Promise<AutomaticCampaignDto[]> {
    const response = await axios.get<AutomaticCampaignDto[]>(
      ApiRoutes.AUTOMATIC_CAMPAIGNS,
      { params: { tenant } },
    );
    return response.data;
  },

  /** Toggle an automatic campaign on/off. */
  async setAutomaticEnabled(
    key: string,
    enabled: boolean,
    tenant: "fish" | "meat" = "fish",
  ): Promise<void> {
    // Keys are unique per tenant only; omitting tenant hits the fish row.
    await axios.patch(
      `${ApiRoutes.AUTOMATIC_CAMPAIGNS}/${key}`,
      { enabled },
      { params: { tenant } },
    );
  },
};
